import { Grid, InputAdornment, SvgIcon } from '@mui/material'
import {
  CA as CAFlag,
  DE as DEFlag,
  MX as MXFlag,
} from 'country-flag-icons/react/3x2'
import dayjs from 'dayjs'
import { useFormikContext } from 'formik'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetCities } from '../../../hooks/api/cities/useGetCities'
import { useGetCountries } from '../../../hooks/api/countries/useGetCountries'
import {
  CityData,
  COUNTRIES,
  CountryData,
  PaginatedData,
} from '../../../types/interfaces'
import Phonefield from '../forms/PhonefieldWrapper'
import SelectDate from '../forms/SelectDateWrapper'
import Select from '../forms/SelectWrapper'
import Textfield from '../forms/TextfieldWrapper'

const CountryFlagIcon: { [key: number]: ReactElement } = {
  [COUNTRIES.CANADA]: <CAFlag title="Canada" />,
  [COUNTRIES.GERMANY]: <DEFlag title="Germany" />,
  [COUNTRIES.MEXICO]: <MXFlag title="Mexico" />,
}

const COUNTRY_PHONE_MASKS: { [key: number]: string } = {
  [COUNTRIES.CANADA]: '+1(999)999-9999',
  [COUNTRIES.GERMANY]: '+49(999)999-9999',
  [COUNTRIES.MEXICO]: '+52(999)999-9999',
}

const PersonalInfo = () => {
  const { values } = useFormikContext<{ country: number }>()

  const todayDate = dayjs().format('YYYY-MM-DD')
  const { t } = useTranslation()

  const selectCities = useCallback(
    (data: PaginatedData<CityData[]>) =>
      data.items
        .map(({ city_id, min_age, name }: CityData) => ({
          id: city_id,
          min_age,
          name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    []
  )

  const selectCountries = useCallback(
    (data: PaginatedData<CountryData[]>) =>
      data.items
        .map(({ id, name }: CountryData) => ({
          id,
          name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    []
  )

  const getCitiesFilter = useMemo(
    () => ({
      country_id: values?.country,
    }),
    [values?.country]
  )

  const { data: countries, isLoading: isLoadingCountries } = useGetCountries({
    select: selectCountries,
  })

  const { data: cities, isLoading } = useGetCities({
    enabled: !!values?.country,
    filters: getCitiesFilter,
    select: selectCities,
  })

  const configGridItem = {
    item: true,
    xs: 12,
  }

  return (
    <Grid
      container
      rowGap={4}
      sx={{
        mb: 2,
        p: 4,
      }}
    >
      <Grid {...configGridItem}>
        <Textfield label={t('auth.register.firstname')} name="firstName" />
      </Grid>
      <Grid {...configGridItem}>
        <Textfield label={t('auth.register.lastname')} name="lastName" />
      </Grid>
      <Grid {...configGridItem}>
        <Phonefield
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgIcon>
                  {CountryFlagIcon[values?.country] ||
                    CountryFlagIcon[COUNTRIES.CANADA]}
                </SvgIcon>
              </InputAdornment>
            ),
          }}
          label={t('profile.company.phone')}
          mask={COUNTRY_PHONE_MASKS[values?.country]}
          name="phoneNumber"
        />
      </Grid>
      <Grid {...configGridItem}>
        <Select
          label={t('profile.info.country')}
          name="country"
          options={countries || []}
        />
      </Grid>
      <Grid {...configGridItem}>
        {isLoading ? (
          <> {t('profile.info.loading_cities')}...</>
        ) : (
          <Select
            disabled={isLoadingCountries}
            label={t('profile.info.city')}
            name="city"
            options={cities || []}
          />
        )}
      </Grid>
      <Grid {...configGridItem}>
        <Textfield label={t('profile.info.address')} name="address" />
      </Grid>
      <Grid {...configGridItem}>
        <SelectDate
          label={t('profile.personal.birthdate')}
          maxDate={todayDate}
          name="dateOfBirth"
        />
      </Grid>
    </Grid>
  )
}

export default PersonalInfo
